import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import { useSelector } from "react-redux";
import { Col, Row, Spinner, Table } from "reactstrap";
import logo from "../../assets/imgs/pdf-logo.png";
import GeneralButton from "../../components/button/GeneralButton";
import PageName from "../../components/react_helmet/PageName";
import { getDocByKeyValue, saveData } from "../../firebase/utility";
import {
  formatNumberWithCommas,
  sendCancellationEmail,
  uploadBlobAndSendEmail,
} from "../../helpers/helpers";
import useUpdatePurchaseOrder from "./customHooks/useUpdatePurchaseOrder";
import {
  submitPOtoDnh,
  submitToIngram,
  submitToAsi,
  submitPurchaseOrderToTd,
  submitPurchaseOrderToAccutech,
  getJenneQuoteForPO,
  submitPurchaseOrderToJenne,
  submitPurchaseOrderToBluestar,
} from "../../api/edi.api";
import JenneBackorderModal from "../../components/modals/JenneBackorderModal";
import JenneQuotePreviewModal from "../../components/modals/JenneQuotePreviewModal";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { DOCUMENT_PDF_STYLE, DOCUMENT_PRINT_STYLE } from "../../utils/documentPdf";
import useDocumentPdf from "../../hooks/useDocumentPdf";
import DocumentPdfPreview from "../../components/pdf/DocumentPdfPreview";

// Clean and normalize text for display and PDF rendering
const cleanDescription = (raw) => {
  if (!raw && raw !== 0) return "N/A";
  let str = String(raw);
  // Replace <br> and </p> with spaces / newlines
  str = str.replace(/<br\s*\/?>/gi, " ").replace(/<\/p>/gi, "\n");
  // Strip all remaining HTML tags
  str = str.replace(/<[^>]+>/g, " ");
  // Decode common HTML entities
  str = str
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&ndash;/gi, "-")
    .replace(/&mdash;/gi, "-");
  // Replace non-breaking spaces and special unicode whitespaces with standard space
  str = str.replace(/[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, " ");
  // Normalize consecutive spaces/tabs to a single space
  str = str.replace(/[ \t]+/g, " ");
  return str.trim() || "N/A";
};

// Remove any embedded blind shipping directive notes from raw vendor instructions
const getCleanVendorInstructions = (raw) => {
  if (!raw && raw !== 0) return "";
  let text = String(raw);
  text = text
    .replace(/\*+\s*BLIND SHIPPING ONLY\s*\*+[\s\S]*?(inside or outside the package\.?)/gi, "")
    .replace(/\*+\s*BLIND SHIPMENT DIRECTIVE:?[\s\S]*?(inside or outside the package\.?)/gi, "")
    .replace(/Please do NOT include any (?:pricing|pring) on invoices, receipts, price tags, or promotional materials inside or outside the package\.?/gi, "")
    .replace(/\*+\s*BLIND SHIPPING ONLY\s*\*+/gi, "");
  return text.trim();
};

const PrintPurchaseOrderPdf = (props) => {
  const {
    state,
    company,
    history,
    type,
  } = useUpdatePurchaseOrder(props);
  const routerHistory = useHistory();
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [dnhLoading, setDnhLoading] = useState(false);
  const [ingramLoading, setIngramLoading] = useState(false);
  const [asiLoading, setAsiLoading] = useState(false);
  const [tdLoading, setTdLoading] = useState(false);
  const [accutechLoading, setAccutechLoading] = useState(false);
  const [bluestarLoading, setBluestarLoading] = useState(false);

  const navigateToPurchaseOrders = () => {
    const nav = typeof history?.push === "function" ? history : routerHistory;
    if (nav && typeof nav.push === "function") {
      nav.push("/admin/purchase-orders");
    }
  };

  // Jenne specific states
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [jenneQuoteLoading, setJenneQuoteLoading] = useState(false);
  const [jenneQuoteData, setJenneQuoteData] = useState(null);
  const [jenneSubmitLoading, setJenneSubmitLoading] = useState(false);
  const [backorderModalOpen, setBackorderModalOpen] = useState(false);
  const [backorderedItems, setBackorderedItems] = useState([]);
  const [submittedOrderNumber, setSubmittedOrderNumber] = useState("");
  const [submittedQuoteNumber, setSubmittedQuoteNumber] = useState("");

  const [includeBlindShipNote, setIncludeBlindShipNote] = useState(true);

  const poFileName = `PO#${details?.po_no || details?.id || "order"}.pdf`;

  const printContentRef = useRef(null);
  const pdf = useDocumentPdf(
    printContentRef,
    JSON.stringify([details, company, state?.shipping_total, includeBlindShipNote]),
    poFileName,
    Boolean(details) && !pageLoader
  );

  const sendPdfToEmail = async () => {
    setLoading(true);
    try {
      if (type === "cancel") {
        const isSuccess = await sendCancellationEmail(
          details?.vendor?.email ?? state?.vendor?.billing[0]?.email,
          "Purchase Order Cancelled",
          details?.id,
          user?.email,
          setLoading,
          type
        );
        if (isSuccess) {
          await saveData("PurchaseOrders", details?._id, {
            purchaseOrderStatus: "pending cancellation",
          });
          navigateToPurchaseOrders();
        }
      } else {
        const isSuccess = await uploadBlobAndSendEmail(
          pdf.getBlob(),
          "purchaseOrderPDF",
          details?._id,
          "Purchase Order",
          "PurchaseOrders",
          setLoading,
          details?.id,
          details?.vendor?.email ?? state?.vendor?.billing[0]?.email,
          type,
          details?.pdfLink,
          user?.email
        );
        if (isSuccess) {
          navigateToPurchaseOrders();
        }
      }
    } catch (error) {
      console.error("Error in sendPdfToEmail:", error);
      toast.error("An error occurred while generating or sending the PDF.");
    } finally {
      setLoading(false);
    }
  };

  const sendToIngram = () => {
    if (!details?._id) {
      toast.error("Purchase Order ID not found");
      return;
    }
    try {
      setIngramLoading(true);
      submitToIngram(details?._id, (res) => {
        if (res?.status === 200) {
          toast.success(res?.data?.message || "PO submitted successfully");
          setIngramLoading(false);
          navigateToPurchaseOrders();
        } else {
          toast.error("Something went wrong! Please try again.");
          setIngramLoading(false);
        }
      });
    } catch (error) {
      console.error("Error while submitting PO to Ingram:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unexpected error occurred while submitting the PO.";
      setIngramLoading(false);
      toast.error(errorMessage);
    }
  };

  const sendToEdi = () => {
    if (!details?._id) {
      toast.error("Purchase Order ID not found");
      return;
    }
    try {
      setDnhLoading(true);
      submitPOtoDnh(details?._id, (res) => {
        if (res?.status === 200) {
          toast.success(res?.data?.message || "PO submitted successfully");
          setDnhLoading(false);
          navigateToPurchaseOrders();
        } else {
          toast.error("Something went wrong! Please try again.");
          setDnhLoading(false);
        }
      });
    } catch (error) {
      console.error("Error while submitting PO to DNH:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unexpected error occurred while submitting the PO.";
      setDnhLoading(false);
      toast.error(errorMessage);
    }
  };

  const sendToAsi = () => {
    if (!details?._id) {
      toast.error("Purchase Order ID not found");
      return;
    }
    try {
      setAsiLoading(true);
      submitToAsi(details?._id, (res) => {
        if (res?.status === 200) {
          toast.success(res?.data?.message || "PO submitted to ASI successfully");
          setAsiLoading(false);
          navigateToPurchaseOrders();
        } else {
          toast.error(res?.message || "Something went wrong! Please try again.");
          setAsiLoading(false);
        }
      });
    } catch (error) {
      console.error("Error while submitting PO to ASI:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unexpected error occurred while submitting the PO.";
      setAsiLoading(false);
      toast.error(errorMessage);
    }
  };

  const sendToTd = () => {
    if (!details?._id) {
      toast.error("Purchase Order ID not found");
      return;
    }
    try {
      setTdLoading(true);
      submitPurchaseOrderToTd(details?._id, (res) => {
        setTdLoading(false);
        if (res?.status === 200 && (res?.data?.success || res?.success)) {
          toast.success(res?.data?.message || res?.message || "PO submitted to TeleDynamics successfully");
          navigateToPurchaseOrders();
        } else {
          toast.error(res?.message || res?.data?.message || "Something went wrong! Please try again.");
        }
      });
    } catch (error) {
      console.error("Error while submitting PO to TeleDynamics:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unexpected error occurred while submitting the PO.";
      setTdLoading(false);
      toast.error(errorMessage);
    }
  };

  const sendToAccutech = () => {
    if (!details?._id) {
      toast.error("Purchase Order ID not found");
      return;
    }
    try {
      setAccutechLoading(true);
      submitPurchaseOrderToAccutech(details?._id, (res) => {
        setAccutechLoading(false);
        if (res?.status === 200 && (res?.data?.success || res?.success)) {
          const lines = res?.data?.data?.lines || [];
          const rejectedLines = lines.filter((l) => l?.status === "REJECTED");
          if (rejectedLines.length > 0) {
            const rejectedMsg = rejectedLines
              .map((l) => l?.statusMessages?.join(", ") || `Line ${l?.linenumber} rejected`)
              .join("; ");
            toast.error(`PO submitted with rejected lines: ${rejectedMsg}`);
          } else {
            toast.success(res?.data?.message || res?.message || "PO submitted to AccutechData successfully");
            navigateToPurchaseOrders();
          }
        } else {
          toast.error(res?.message || res?.data?.message || "Something went wrong! Please try again.");
        }
      });
    } catch (error) {
      console.error("Error while submitting PO to Accutech:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unexpected error occurred while submitting the PO.";
      setAccutechLoading(false);
      toast.error(errorMessage);
    }
  };

  const sendToBluestar = () => {
    if (!details?._id) {
      toast.error("Purchase Order ID not found");
      return;
    }
    try {
      setBluestarLoading(true);
      submitPurchaseOrderToBluestar(details?._id, (res) => {
        setBluestarLoading(false);
        if (res?.status === 200 && (res?.data?.success || res?.success)) {
          const resData = res?.data?.data || res?.data;
          const orderNum =
            resData?.orderNumber ||
            resData?.vendorOrderNumber ||
            resData?.salesOrderNumber ||
            resData?.poNumber ||
            "";

          if (orderNum) {
            Promise.resolve(
              saveData("PurchaseOrders", details?._id, {
                vendorPoNumber: orderNum,
                purchaseOrderStatus: "active",
                orderStatus: "submitted",
              })
            ).catch(console.error);

            setDetails((prev) => ({
              ...prev,
              vendorPoNumber: orderNum,
              purchaseOrderStatus: "active",
            }));
          }

          toast.success(
            res?.data?.message ||
            res?.message ||
            `PO submitted to BlueStar successfully! ${orderNum ? `Order #: ${orderNum}` : ""}`
          );
          navigateToPurchaseOrders();
        } else {
          toast.error(
            res?.data?.error ||
            res?.data?.message ||
            res?.message ||
            "Failed to submit Purchase Order to BlueStar. Please try again."
          );
        }
      });
    } catch (error) {
      console.error("Error while submitting PO to BlueStar:", error);
      setBluestarLoading(false);
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Unexpected error occurred while submitting the PO."
      );
    }
  };

  const fetchJenneQuote = async (poId) => {
    const targetId = poId || details?._id || id;
    if (!targetId) return;

    // Ensure ShipToPhone is present for Jenne
    if (details && (!details?.shipping?.phone || details?.shipping?.phone.trim() === "")) {
      const fallbackPhone = "6305261800";
      const updatedShipping = {
        ...(details?.shipping || {}),
        phone: fallbackPhone,
      };
      try {
        await saveData("PurchaseOrders", targetId, {
          shipping: updatedShipping,
        });
        setDetails((prev) => ({
          ...prev,
          shipping: updatedShipping,
        }));
      } catch (err) {
        console.warn("Could not auto-populate shipping phone:", err);
      }
    }

    setJenneQuoteLoading(true);
    getJenneQuoteForPO(targetId, (res) => {
      setJenneQuoteLoading(false);
      if (res?.status === 200 && (res?.data?.success || res?.success)) {
        const qData = res?.data?.data || res?.data;
        setJenneQuoteData(qData);
        setQuoteModalOpen(true);
        if (qData?.quoteNumber) {
          toast.success(`Jenne Quote #${qData.quoteNumber} fetched successfully`);
        }
      } else {
        const errMsg =
          res?.data?.error ||
          res?.message ||
          res?.data?.message ||
          "Failed to fetch quotation from Jenne.";
        toast.error(errMsg);
      }
    });
  };

  const sendToJenne = async () => {
    if (!details?._id) {
      toast.error("Purchase Order ID not found");
      return;
    }
    try {
      // Ensure ShipToPhone is present for Jenne
      if (!details?.shipping?.phone || details?.shipping?.phone.trim() === "") {
        const fallbackPhone = "6305261800";
        const updatedShipping = {
          ...(details?.shipping || {}),
          phone: fallbackPhone,
        };
        try {
          await saveData("PurchaseOrders", details._id, {
            shipping: updatedShipping,
          });
          setDetails((prev) => ({
            ...prev,
            shipping: updatedShipping,
          }));
        } catch (err) {
          console.warn("Could not auto-populate shipping phone:", err);
        }
      }

      setJenneSubmitLoading(true);
      submitPurchaseOrderToJenne(details?._id, async (res) => {
        setJenneSubmitLoading(false);
        if (res?.status === 200 && (res?.data?.success || res?.success)) {
          const resData = res?.data?.data || res?.data;
          const orderNum = resData?.orderNumber || "";
          const quoteNum = resData?.quoteNumber || jenneQuoteData?.quoteNumber || "";
          const backorders = resData?.backorderedItems || [];

          setSubmittedOrderNumber(orderNum);
          setSubmittedQuoteNumber(quoteNum);
          setQuoteModalOpen(false);

          try {
            await saveData("PurchaseOrders", details._id, {
              vendorPoNumber: orderNum,
              jenneQuoteNumber: quoteNum,
              purchaseOrderStatus: "active",
              orderStatus: "submitted",
            });
            setDetails((prev) => ({
              ...prev,
              vendorPoNumber: orderNum,
              jenneQuoteNumber: quoteNum,
              purchaseOrderStatus: "active",
            }));
          } catch (dbErr) {
            console.error("Error updating PO in database:", dbErr);
          }

          toast.success(
            res?.data?.message ||
            `Purchase order submitted to Jenne successfully! Order #: ${orderNum}`
          );

          if (backorders && backorders.length > 0) {
            setBackorderedItems(backorders);
            setBackorderModalOpen(true);
          } else {
            navigateToPurchaseOrders();
          }
        } else {
          toast.error(
            res?.data?.error ||
            res?.message ||
            res?.data?.message ||
            "Failed to submit Purchase Order to Jenne. Please try again."
          );
        }
      });
    } catch (error) {
      console.error("Error while submitting PO to Jenne:", error);
      setJenneSubmitLoading(false);
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Unexpected error occurred while submitting the PO."
      );
    }
  };

  useEffect(() => {
    if (id) {
      setPageLoader(true);
      getDocByKeyValue("PurchaseOrders", "_id", id).then((res) => {
        if (res) {
          let data = res[0];
          setDetails(data);
          setPageLoader(false);
        } else {
          setPageLoader(false);
          console.log("PDF information is not coming from DB");
        }
      });
    }
  }, [id]);

  const vendorName = (
    details?.vendor?.company ||
    details?.vendor?.name ||
    details?.vendorName ||
    details?.company ||
    state?.vendor?.billing?.[0]?.company ||
    state?.vendor?.billing?.[0]?.name ||
    state?.vendor?.company ||
    state?.vendor?.name ||
    state?.company ||
    state?.vendorName ||
    ""
  ).toLowerCase();

  const isAsiVendor = vendorName.includes("asi");
  const isIngramVendor = vendorName.includes("ingram");
  const isTdVendor = vendorName.includes("teledynamics") || vendorName.includes("tele dynamics") || vendorName.includes("tele-dynamics") || vendorName === "td";
  const isAccutechVendor = vendorName.includes("accutech") || vendorName.includes("accutechdata");
  const isDnhVendor = vendorName.includes("d&h") || vendorName.includes("dnh");
  const isJenneVendor = vendorName.includes("jenne");
  const isBluestarVendor =
    vendorName.includes("bluestar") ||
    vendorName.includes("blue star") ||
    vendorName.includes("blue-star");

  if (pageLoader) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner color="primary" style={{ width: "5rem", height: "5rem" }} />
      </div>
    );
  }

  return (
    <>
      <PageName title={"Purchase Details"} />
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 px-4 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          {/* Optional Toggle for Blind Shipping Note */}
          <div className="d-flex align-items-center gap-2 bg-white px-3 py-1 rounded-3 shadow-sm border">
            <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
              <input
                className="form-check-input pointer"
                type="checkbox"
                id="toggleBlindShipNote"
                checked={includeBlindShipNote}
                onChange={(e) => setIncludeBlindShipNote(e.target.checked)}
                style={{ width: "2em", height: "1.1em", cursor: "pointer" }}
              />
              <label
                className="form-check-label mb-0 fw-semibold text-dark pointer"
                htmlFor="toggleBlindShipNote"
                style={{ fontSize: "13px", cursor: "pointer", userSelect: "none" }}
              >
                Include Blind Shipping Note in PDF
              </label>
            </div>
          </div>
        </div>

        <IoCloseSharp
          size={30}
          onClick={() =>
            state?.saleOrderCheck
              ? history.push({
                  pathname: `/admin/sale-order-details/${details?.saleOrderId}`,
                  state: { fromPOPdf: true },
                })
              : history.goBack()
          }
          className="pointer hover-opacity-75"
        />
      </div>

      <DocumentPdfPreview pdf={pdf} title="Purchase order" />
      <div className="document-pdf-source" aria-hidden="true">
        <section
          className="bg-white shadow-sm pdf-container document-pdf"
          ref={printContentRef}
          style={{
            color: "#333",
            fontSize: "13.5px",
            border: "1px solid #dee2e6",
            margin: "0 auto",
            backgroundColor: "#ffffff",
          }}
        >
          <style>{DOCUMENT_PDF_STYLE + DOCUMENT_PRINT_STYLE}</style>

          {/* Header */}
          <div data-pdf-keep className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
            <img src={logo} style={{ height: "auto", maxWidth: "240px" }} alt="Tech Micro USA" />
            <div className="text-end">
              <h2 className="mb-1 fw-bold pdf-title" style={{ color: "#1a3b5c", letterSpacing: "1px", textTransform: "uppercase", fontSize: "28px" }}>
                PURCHASE ORDER
              </h2>
              <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>Date: {moment(details?.created_at || details?.date_created || Date.now()).format("MMM DD, YYYY")}</p>
              <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>PO #: {details?.po_no || details?.id || "null"}</p>
            </div>
          </div>

          {/* Row 1: Company Details & Order Information */}
          <div data-pdf-keep className="row-2-col mb-4" style={{ display: "flex", flexDirection: "row", width: "100%", gap: "16px" }}>
            <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
              <div className="border rounded h-100" style={{ backgroundColor: "#f8f9fa", overflow: "hidden" }}>
                <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Company Details</h6>
                <div className="p-3">
                  <p className="mb-1" style={{ fontSize: "14px" }}><strong>Techmicro USA</strong></p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>206 Far Hills Drive</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>Bolingbrook, IL 60440</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>Sales No: (630) 526 1800</p>
                  <p className="mb-0" style={{ fontSize: "14px" }}>www.techmicrousa.com</p>
                </div>
              </div>
            </div>
            <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
              <div className="border rounded h-100" style={{ backgroundColor: "#f8f9fa", overflow: "hidden" }}>
                <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Order Information</h6>
                <div className="p-3">
                  <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                    <span className="fw-bold" style={{ fontSize: "14px" }}>Vendor ID</span>
                    <span style={{ fontSize: "14px" }}>{details?.vendorId ?? "5"}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                    <span className="fw-bold" style={{ fontSize: "14px" }}>PO Number</span>
                    <span style={{ fontSize: "14px" }}>{details?.po_no ?? "null"}</span>
                  </div>
                  {details?.jenneQuoteNumber && (
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Jenne Quote #</span>
                      <span className="fw-bold text-primary" style={{ fontSize: "14px" }}>#{details.jenneQuoteNumber}</span>
                    </div>
                  )}
                  {details?.vendorPoNumber && (
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Vendor Order #</span>
                      <span className="fw-bold text-success" style={{ fontSize: "14px" }}>{details.vendorPoNumber}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between mb-2 pb-2 border-bottom align-items-center">
                    <span className="fw-bold" style={{ fontSize: "14px" }}>Shipping</span>
                    <span style={{ fontSize: "14px" }}>
                      {details?.shippingVia || details?.shipping_lines?.[0]?.method_title || "Ground 5 - 7 Days"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                    <span className="fw-bold" style={{ fontSize: "14px" }}>Payment Terms</span>
                    <span style={{ fontSize: "14px" }}>{details?.vendor?.terms || "Due on Receipt"}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                    <span className="fw-bold" style={{ fontSize: "14px" }}>Company</span>
                    <span style={{ fontSize: "14px" }}>{details?.company === "" ? "N/A" : details?.company ?? company ?? "FEDEX"}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold" style={{ fontSize: "14px" }}>Freight Billing Account</span>
                    <span style={{ fontSize: "14px" }}>{details?.freightBillingAccount || "N/A"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Bill To, Ship To, Issued To */}
          <div data-pdf-keep className="row-3-col mb-4" style={{ display: "flex", flexDirection: "row", width: "100%", gap: "16px" }}>
            <div className="col-third" style={{ flex: "1 1 33.333%", width: "33.333%" }}>
              <div className="border rounded h-100" style={{ overflow: "hidden" }}>
                <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Bill To</h6>
                <div className="p-3">
                  <p className="mb-1 fw-bold" style={{ fontSize: "14px" }}>TechMicro USA</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>206 Far Hills Drive</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>Bolingbrook, IL 60440</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>Ph: 630-526-1800</p>
                </div>
              </div>
            </div>
            <div className="col-third" style={{ flex: "1 1 33.333%", width: "33.333%" }}>
              <div className="border rounded h-100" style={{ overflow: "hidden" }}>
                <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Ship To</h6>
                <div className="p-3">
                  <p className="mb-1 fw-bold" style={{ fontSize: "14px" }}>{details?.shipping?.company || "TechMicro USA INC"}</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>{details?.shipping?.first_name || details?.shipping?.last_name ? `${details?.shipping?.first_name || ""} ${details?.shipping?.last_name || ""}` : "Receiving Department"}</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>{details?.shipping?.address_1 || "206 Far Hill Drive"}</p>
                  {details?.shipping?.address_2 && <p className="mb-1" style={{ fontSize: "14px" }}>{details?.shipping?.address_2}</p>}
                  <p className="mb-1" style={{ fontSize: "14px" }}>{`${details?.shipping?.city || "Bolingbrook"}, ${details?.shipping?.state || "IL"} ${details?.shipping?.postcode || "60440"}`}</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>Ph: {details?.shipping?.phone || "Not Available"}</p>
                </div>
              </div>
            </div>
            <div className="col-third" style={{ flex: "1 1 33.333%", width: "33.333%" }}>
              <div className="border rounded h-100" style={{ overflow: "hidden" }}>
                <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Issued To</h6>
                <div className="p-3">
                  <p className="mb-1 fw-bold" style={{ fontSize: "14px" }}>{details?.vendor?.company || details?.vendor?.name || "eBay Inc"}</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>{details?.vendor?.address_1 || "2025 Hamilton Avenue,"}</p>
                  {details?.vendor?.address_2 && <p className="mb-1" style={{ fontSize: "14px" }}>{details?.vendor?.address_2}</p>}
                  <p className="mb-1" style={{ fontSize: "14px" }}>{`${details?.vendor?.city || "San Jose"}, ${details?.vendor?.state || "CA"} ${details?.vendor?.postcode || "95125"}`}</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>Ph: {details?.vendor?.phone || "+18665403229"}</p>
                  <p className="mb-1" style={{ fontSize: "14px" }}>{details?.vendor?.email || "Ebay@techmicrousa.com"}</p>
                  {details?.vendor?.website && <p className="mb-1" style={{ fontSize: "14px" }}>{details?.vendor?.website}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Items Table */}
          <div className="mb-4">
            <Table bordered responsive hover className="mb-0" style={{ fontSize: "14px", tableLayout: "fixed", width: "100%" }}>
              <thead style={{ backgroundColor: "#f0f0f0", color: "#000" }}>
                <tr className="text-center">
                  <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "15%" }}>ITEM #</th>
                  <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "13%" }}>PART NO.</th>
                  <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "6%" }}>QTY</th>
                  <th className="py-2 fw-bold text-uppercase text-start" style={{ fontSize: "12px", width: "36%" }}>DESCRIPTION</th>
                  <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "13%", whiteSpace: "nowrap" }}>PRICE</th>
                  <th className="py-2 fw-bold text-uppercase text-end" style={{ fontSize: "12px", width: "17%", whiteSpace: "nowrap" }}>EXT PRICE</th>
                </tr>
              </thead>
              <tbody>
                {details?.line_items?.map((item, index) => (
                  <tr className="text-center align-middle" key={index}>
                    <td className="py-2" style={{ wordBreak: "break-all" }}>{item?.sku || "-"}</td>
                    <td className="py-2" style={{ wordBreak: "break-all" }}>{item?.sku?.slice(4) || item?.sku || "-"}</td>
                    <td className="py-2">{item?.quantity ?? 0}</td>
                    <td className="py-2 text-start" style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}>
                      {cleanDescription(item?.description)}
                    </td>
                    <td className="py-2" style={{ whiteSpace: "nowrap" }}>${formatNumberWithCommas(Number(item?.price || 0).toFixed(2))}</td>
                    <td className="py-2 text-end fw-bold" style={{ whiteSpace: "nowrap" }}>${formatNumberWithCommas(((Number(item?.quantity) || 0) * (Number(item?.price) || 0)).toFixed(2))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Row 4: Notes and Totals */}
          <div className="pdf-summary row-2-col mt-4" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%", gap: "16px" }}>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <div className="border rounded mb-0 bg-light" style={{ overflow: "hidden" }}>
                <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px" }}>
                  Vendor Notes & Instructions
                </h6>
                <div className="p-3" style={{ fontSize: "13px", wordBreak: "break-word" }}>
                  {(() => {
                    const customInstructions = getCleanVendorInstructions(details?.vendorInstructions);
                    const shouldShowBlindNote = includeBlindShipNote;
                    const displayCustom = customInstructions;

                    return (
                      <>
                        {shouldShowBlindNote && (
                          <p className={displayCustom ? "mb-2" : "mb-0"} style={{ fontSize: "13px", color: "#000", lineHeight: "1.5" }}>
                            <strong>************BLIND SHIPPING ONLY************</strong>
                            <br />
                            Please do NOT include any pricing on invoices, receipts, price tags, or promotional materials inside or outside the package
                          </p>
                        )}
                        {displayCustom ? (
                          <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{displayCustom}</p>
                        ) : (
                          !shouldShowBlindNote && (
                            <p className="mb-0 text-muted">No vendor instructions provided.</p>
                          )
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="totals-card" style={{ flex: "0 0 320px", width: "320px", minWidth: "320px", marginLeft: "auto" }}>
              <div className="border rounded" style={{ overflow: "hidden" }}>
                <div className="d-flex justify-content-between p-2 border-bottom bg-light" style={{ fontSize: "14px" }}>
                  <span className="fw-bold">Sub Total:</span>
                  <span>
                    ${formatNumberWithCommas(Number(
                      details?.subTotal || details?.subtotal || (details?.line_items?.reduce((acc, it) => acc + (Number(it?.price || 0) * Number(it?.quantity || 0)), 0)) || 0
                    ).toFixed(2))}
                  </span>
                </div>
                <div className="d-flex justify-content-between p-2 border-bottom" style={{ fontSize: "14px" }}>
                  <span>Tax:</span>
                  <span>
                    ${formatNumberWithCommas(parseFloat(details?.totalTaxAmount?.tax || 0).toFixed(2))}
                  </span>
                </div>
                <div className="d-flex justify-content-between p-2 border-bottom" style={{ fontSize: "14px" }}>
                  <span>Shipping:</span>
                  <span>${formatNumberWithCommas(parseFloat(state?.shipping_total ? state?.shipping_total : (details?.totalTaxAmount?.shipping || 0)).toFixed(2))}</span>
                </div>
                <div className="d-flex justify-content-between p-3" style={{ fontSize: "16px", backgroundColor: "#f0f0f0", color: "#000" }}>
                  <span className="fw-bold text-uppercase">ORDER TOTAL:</span>
                  <span className="fw-bold">
                    ${formatNumberWithCommas(parseFloat(
                      details?.total || details?.totalAmount || (Number(details?.subTotal || 0) + parseFloat(details?.totalTaxAmount?.tax || 0) + parseFloat(details?.totalTaxAmount?.shipping || 0)) || 0
                    ).toFixed(2))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Row>
        <Col md={10} className="d-flex flex-wrap justify-content-end mt-3 gap-2 mx-auto">
          {/* Dynamic Electronic Dispatch Button: Only 1 button shown based on vendor */}
          {isJenneVendor ? (
            <button
              type="button"
              className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-3 py-2 fw-semibold rounded-2 shadow-sm bg-white mb-3"
              onClick={() => fetchJenneQuote(details?._id)}
              disabled={jenneQuoteLoading || jenneSubmitLoading}
              style={{ fontSize: "14px", height: "38px" }}
            >
              {jenneQuoteLoading ? (
                <>
                  <Spinner size="sm" color="primary" />
                  <span>Fetching New Quotation...</span>
                </>
              ) : (
                <>
                  <FiRefreshCw size={15} />
                  <span>Get New Quotation from Jenne</span>
                </>
              )}
            </button>
          ) : isBluestarVendor ? (
            <GeneralButton
              title={bluestarLoading ? "Sending to BlueStar..." : "Send to BlueStar"}
              className={"blue-button mb-3"}
              handleClick={sendToBluestar}
              loading={bluestarLoading}
              disabled={bluestarLoading}
            />
          ) : isAsiVendor ? (
            <GeneralButton
              title={asiLoading ? "Sending to ASI..." : "Send to ASI"}
              className={"blue-button mb-3"}
              handleClick={sendToAsi}
              loading={asiLoading}
              disabled={asiLoading}
            />
          ) : isIngramVendor ? (
            <GeneralButton
              title={
                ingramLoading ? "Sending to Ingram..." : "Send to Ingram"
              }
              className={"blue-button mb-3"}
              handleClick={sendToIngram}
              loading={ingramLoading}
              disabled={ingramLoading}
            />
          ) : isTdVendor ? (
            <GeneralButton
              title={
                tdLoading ? "Sending to TeleDynamics..." : "Send to TeleDynamics"
              }
              className={"blue-button mb-3"}
              handleClick={sendToTd}
              loading={tdLoading}
              disabled={tdLoading}
            />
          ) : isAccutechVendor ? (
            <GeneralButton
              title={
                accutechLoading ? "Sending to Accutech..." : "Send to Accutech"
              }
              className={"blue-button mb-3"}
              handleClick={sendToAccutech}
              loading={accutechLoading}
              disabled={accutechLoading}
            />
          ) : isDnhVendor ? (
            <GeneralButton
              title={dnhLoading ? "Sending to EDI (D&H)..." : "Send to EDI (D&H)"}
              className={"blue-button mb-3"}
              handleClick={sendToEdi}
              loading={dnhLoading}
              disabled={dnhLoading}
            />
          ) : (
            <GeneralButton
              title={dnhLoading ? "Sending to EDI..." : "Send to EDI"}
              className={"blue-button mb-3"}
              handleClick={sendToEdi}
              loading={dnhLoading}
              disabled={dnhLoading}
            />
          )}

          {type === "cancel" ? (
            !details?.shippingCheckboxes?.do_not_email && (
              <GeneralButton
                title={"Send Cancellation Email"}
                className={"blue-button mb-3"}
                width={"300px"}
                handleClick={sendPdfToEmail}
                loading={loading}
                disabled={loading}
              />
            )
          ) : (
            <GeneralButton
              title={"Download PDF"}
              className={"blue-button mb-3"}
              width={"220px"}
              handleClick={pdf.download}
              disabled={!pdf.ready}
            />
          )}
          {details?.purchaseOrderStatus === "cancel" ? (
            <GeneralButton
              title={"Purchase Order has been cancelled"}
              className={"bg-danger border-0 rounded-0 mb-3"}
            />
          ) : (
            !details?.shippingCheckboxes?.do_not_email && (
              <GeneralButton
                title={"Send PDF"}
                className={"blue-button mb-3"}
                handleClick={sendPdfToEmail}
                loading={loading}
                disabled={loading || (type !== "cancel" && !pdf.ready)}
              />
            )
          )}
        </Col>
      </Row>

      <JenneQuotePreviewModal
        modal={quoteModalOpen}
        toggle={() => setQuoteModalOpen(!quoteModalOpen)}
        quoteData={jenneQuoteData}
        poDetails={details}
        sendToJenne={sendToJenne}
        jenneSubmitLoading={jenneSubmitLoading}
        onRefreshQuote={() => fetchJenneQuote(details?._id)}
        jenneQuoteLoading={jenneQuoteLoading}
      />

      <JenneBackorderModal
        modal={backorderModalOpen}
        toggle={() => {
          setBackorderModalOpen(false);
          navigateToPurchaseOrders();
        }}
        backorderedItems={backorderedItems}
        orderNumber={submittedOrderNumber}
        quoteNumber={submittedQuoteNumber}
      />
    </>
  );
};
export default PrintPurchaseOrderPdf;

